import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Form, Select } from "antd";
import { MailOutlined, CheckCircleTwoTone } from "@ant-design/icons";

import { userSignup } from "../../redux/slice/rfpModuleSlice/application";
import { AppDispatch, RootState } from "redux/store";

import Input from "style-guide/component/Input";
import Typography from "style-guide/component/Typography";
import { getCookie } from "utils/utils";
import { INTAKE_FLOW_SCRATCH } from "components/dashboard/home/constants";
import Gradient from "style-guide/component/Gradient";
import Button from "style-guide/component/Button";
import Google from "style-guide/svg-components/GoogleSvg";
import MicrosoftSvg from "style-guide/svg-components/MicrosoftSvg";

const SignUp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const isAuthorised = Boolean(getCookie("token"));
  const [form] = Form.useForm();
  const { state } = useLocation();
  const { isLoadingModal } = useSelector(
    (state: RootState) => state.applicationData
  );

  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  useEffect(() => {
    if (isAuthorised) navigate("/home");
  }, [isAuthorised, navigate]);

  useEffect(() => {
    if (!state?.rfp_id) {
      document.cookie =
        "rfp_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    }
  }, [state?.rfp_id]);

  useEffect(() => {
    if (state?.rfp_id) {
      form.setFieldsValue({ email: state?.email });
      form.setFieldsValue({ clientType: state?.client_type });
    }
  }, [state?.email, form, state?.client_type, state?.rfp_id]);

  const onFinish = async (values: any) => {
    const redirectReport = !!state?.newSignup;
    const result = await dispatch(userSignup({...values, redirectReport}));
    if (result?.status === 200) {
      if (state?.email || state?.rfp_id) {
        let path = "";
        if (state?.redirectUrl) {
          path = `${state.redirectUrl}/${state.rfp_id}`;
        } else {
          path = `/build-scratch?rfpId=${state.rfp_id}`;
          localStorage.setItem(INTAKE_FLOW_SCRATCH, "requirement-add");
        }
        navigate(path, { replace: true });
      } else {
        //signup flow, show email verification screen
        setRegistrationSuccess(true);
      }
    }
  };

  return (
    <Gradient>
      <div className="bg-white rounded-2xl shadow-2xl px-8 py-6 w-full max-w-2xl mx-auto">
        {!registrationSuccess ? (
          <>
            <Typography
              text="Create your account"
              variant="Heading 3"
              strong
              className="text-center text-blue-900 mb-4"
            />

            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              className="w-full"
            >
              <div className="flex gap-2">
                <Form.Item
                  label={<Typography text="First Name" variant="Base" />}
                  name="firstName"
                  rules={[
                    { required: true, message: "First name is required." },
                  ]}
                  className="flex-1"
                >
                  <Input
                    placeholder="Enter your first name"
                    size="Medium"
                    disabled={isLoadingModal}
                  />
                </Form.Item>

                <Form.Item
                  label={<Typography text="Last Name" variant="Base" />}
                  name="lastName"
                  rules={[
                    { required: true, message: "Last name is required." },
                  ]}
                  className="flex-1"
                >
                  <Input
                    placeholder="Enter your last name"
                    size="Medium"
                    disabled={isLoadingModal}
                  />
                </Form.Item>
              </div>

              <div className="flex gap-2">
                <Form.Item
                  label={<Typography text="Organisation Name" variant="Base" />}
                  rules={[
                    {
                      required: true,
                      message: "Organisation name is required.",
                    },
                  ]}
                  name="companyName"
                  className="flex-1"
                >
                  <Input
                    placeholder="Organisation name"
                    size="Medium"
                    disabled={isLoadingModal}
                  />
                </Form.Item>

                <Form.Item
                  label={<Typography text="Sector" variant="Base" />}
                  name="clientType"
                  rules={[
                    { required: true, message: "Client type is required." },
                  ]}
                  className="flex-1"
                >
                  <Select
                    size="large"
                    placeholder="Select sector"
                    disabled={!!state?.client_type || isLoadingModal}
                  >
                    <Select.Option value="PUBLIC">Public</Select.Option>
                    <Select.Option value="PRIVATE">Private</Select.Option>
                  </Select>
                </Form.Item>
              </div>

              <Form.Item
                label={
                  <Typography text="Enter your work email" variant="Base" />
                }
                name="email"
                rules={[
                  {
                    required: true,
                    type: "email",
                    message: "Enter a valid email.",
                  },
                ]}
              >
                <Input
                  placeholder="Enter your email"
                  size="Medium"
                  value={state?.email}
                  disabled={!!state?.email || isLoadingModal}
                />
              </Form.Item>

              <Form.Item
                label={<Typography text="Password" variant="Base" />}
                name="password"
                rules={[
                  { required: true, message: "Password is required." },
                  {
                    min: 6,
                    message: "Password must be at least 6 characters.",
                  },
                ]}
              >
                <Input
                  type="password"
                  placeholder="Password"
                  size="Medium"
                  disabled={isLoadingModal}
                />
              </Form.Item>

              <Form.Item
                label={<Typography text="Confirm Password" variant="Base" />}
                name="confirmPassword"
                dependencies={["password"]}
                rules={[
                  { required: true, message: "Please confirm your password." },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Passwords do not match.")
                      );
                    },
                  }),
                ]}
              >
                <Input
                  type="password"
                  placeholder="Confirm password"
                  size="Medium"
                  disabled={isLoadingModal}
                />
              </Form.Item>

              <Form.Item shouldUpdate>
                {() => {
                  const hasErrors = form
                    .getFieldsError()
                    .some(({ errors }) => errors.length > 0);
                  const allTouched = form.isFieldsTouched(true);

                  return (
                    <Button
                      type={
                        !hasErrors && allTouched && !isLoadingModal
                          ? "Primary"
                          : "Disabled"
                      }
                      size="Large"
                      buttonText={
                        isLoadingModal ? "Creating Account..." : "Sign Up"
                      }
                      className="w-full py-3 rounded-lg"
                      loading={isLoadingModal}
                    />
                  );
                }}
              </Form.Item>
            </Form>

            <Typography
              variant="Small"
              className="text-center text-gray-500 text-xs mt-2"
            >
              By signing up, you agree to our Terms of Service. No credit card
              required.
            </Typography>

            <div className="flex items-center space-x-4 my-3">
              <div className="flex-1 h-px bg-gray-300" />
              <Typography
                text="or"
                variant="Base"
                className="text-gray-500 px-3"
              />
              <div className="flex-1 h-px bg-gray-300" />
            </div>

            <div className="flex items-center justify-center gap-2">
              <Button
                type="Default"
                icon
                iconComponent={<Google />}
                buttonText="Sign up with Google"
                className="w-full py-3 border-gray-300 opacity-50 cursor-not-allowed rounded-lg"
              />
              <Button
                type="Default"
                icon
                iconComponent={<MicrosoftSvg />}
                buttonText="Sign up with Microsoft"
                className="w-full py-3 border-gray-300 opacity-50 cursor-not-allowed rounded-lg"
              />
            </div>

            <Typography
              variant="Base"
              className="text-center mt-4 text-gray-600"
            >
              Already have an account?{" "}
              <Link className="text-[#0A4BAA] hover:underline" to="/">
                Login
              </Link>
            </Typography>
          </>
        ) : (
          // ✅ Success Screen
          <div className="flex flex-col items-center space-y-6 text-center py-8">
            <CheckCircleTwoTone twoToneColor="#52c41a" className="text-6xl" />
            <Typography
              text="Registration Successful!"
              variant="Heading 3"
              className="text-green-700 font-bold"
            />
            <Typography
              variant="Base"
              className="text-gray-600 max-w-md"
              text="Your account has been created. We’ve sent a verification email to your inbox. Please check your email and verify your account before logging in."
            />
            <MailOutlined className="text-blue-600 text-5xl mt-2" />
            <Button
              type="Primary"
              buttonText="Go to Login"
              onClick={() => navigate("/")}
              className="w-full py-3 rounded-lg max-w-xs"
            />
          </div>
        )}
      </div>
    </Gradient>
  );
};

export default SignUp;
